import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaUser,
  FaPhone, 
  FaPassport, 
  FaDollarSign, 
  FaUserTie, 
  FaArrowLeft, 
  FaEdit, 
  FaCalendarAlt,
  FaCreditCard,
  FaMoneyBillWave,
  FaChartLine,
  FaHistory,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle
} from 'react-icons/fa';
import { formatDate } from '../../utils/date';

const TravelerDetails = ({ traveler, campaign, onBack, onEdit }) => {
  const { t } = useTranslation('travelers');

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return `0 ${t('common:currency')}`;
    }
    return `${amount.toLocaleString()} ${t('common:currency')}`;
  };

  // يستخدم المنسّق الموحّد المستورد أعلاه؛ أُبقي الاسم كما هو لتفادي تعديل مواضع الاستدعاء

  const getPaymentStatus = () => {
    if (traveler.remainingBalance === 0) return 'paid';
    if (traveler.remainingBalance === traveler.totalAmount) return 'unpaid';
    return 'partial';
  };

  const getPaymentStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <FaCheckCircle className="text-green-500" />;
      case 'partial':
        return <FaExclamationTriangle className="text-yellow-500" />;
      case 'unpaid':
        return <FaTimesCircle className="text-red-500" />;
      default:
        return null;
    }
  };

  const getPaymentProgress = () => {
    const paidAmount = traveler.totalAmount - traveler.remainingBalance;
    const percentage = (paidAmount / traveler.totalAmount) * 100;
    return Math.round(percentage);
  };

  const getNextPaymentInfo = () => {
    if (traveler.paymentType !== 'installments' || !traveler.paymentPlan) {
      return null;
    }

    const { nextPaymentDate, monthlyAmount, paidInstallments, durationMonths } = traveler.paymentPlan;
    const remainingInstallments = durationMonths - (paidInstallments || 0);
    
    return {
      nextDate: nextPaymentDate,
      amount: monthlyAmount,
      remaining: remainingInstallments
    };
  };

  const paymentStatus = getPaymentStatus();
  const nextPayment = getNextPaymentInfo();

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FaArrowLeft className="text-xl" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <FaUser className="text-blue-600" />
              {traveler.fullName}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {campaign ? campaign.campaignName : t('unknownCampaign')}
            </p>
          </div>
        </div>
        <button
          onClick={onEdit}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors"
        >
          <FaEdit />
          {t('edit')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Personal Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FaUser className="text-blue-600" />
              {t('personalInformation')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('fullName')}
                </label>
                <p className="text-gray-900 dark:text-white font-medium">{traveler.fullName}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('passportNumber')}
                </label>
                <p className="text-gray-900 dark:text-white font-medium flex items-center gap-2">
                  <FaPassport className="text-gray-400" />
                  {traveler.passportNumber}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('primaryPhone')}
                </label>
                <p className="text-gray-900 dark:text-white font-medium flex items-center gap-2">
                  <FaPhone className="text-gray-400" />
                  {traveler.primaryPhone}
                </p>
              </div>
              
              {traveler.secondaryPhone && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {t('secondaryPhone')}
                  </label>
                  <p className="text-gray-900 dark:text-white font-medium flex items-center gap-2">
                    <FaPhone className="text-gray-400" />
                    {traveler.secondaryPhone}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Campaign Information */}
          {campaign && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('campaignInformation')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {t('campaignName')}
                  </label>
                  <p className="text-gray-900 dark:text-white font-medium">{campaign.campaignName}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {t('campaignType')}
                  </label>
                  <p className="text-gray-900 dark:text-white font-medium capitalize">
                    {t(`campaignTypes.${campaign.campaignType}`)}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {t('departureDate')}
                  </label>
                  <p className="text-gray-900 dark:text-white font-medium flex items-center gap-2">
                    <FaCalendarAlt className="text-gray-400" />
                    {formatDate(campaign.departureDate)}
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                    {t('returnDate')}
                  </label>
                  <p className="text-gray-900 dark:text-white font-medium flex items-center gap-2">
                    <FaCalendarAlt className="text-gray-400" />
                    {formatDate(campaign.returnDate)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Agent Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FaUserTie className="text-purple-600" />
              {t('agentInformation')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('agentName')}
                </label>
                <p className="text-gray-900 dark:text-white font-medium">{traveler.agentName}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  {t('agentCommission')}
                </label>
                <p className="text-green-600 dark:text-green-400 font-semibold flex items-center gap-2">
                  <FaDollarSign />
                  {formatCurrency(traveler.agentCommission)}
                </p>
              </div>
            </div>
          </div>

          {/* Payment History (Mock) */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FaHistory className="text-gray-600" />
              {t('paymentHistory')}
            </h3>
            
            <div className="space-y-3">
              {/* Mock payment history entries */}
              {traveler.paymentType === 'installments' && traveler.paymentPlan && (
                <div className="border-l-4 border-green-500 pl-4 py-2">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {t('downPayment')}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(traveler.paymentPlan.startDate)}
                      </p>
                    </div>
                    <span className="text-green-600 dark:text-green-400 font-semibold">
                      {formatCurrency(traveler.paymentPlan.downPayment)}
                    </span>
                  </div>
                </div>
              )}
              
              {traveler.paymentType === 'cash' && traveler.remainingBalance === 0 && (
                <div className="border-l-4 border-green-500 pl-4 py-2">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {t('fullPayment')}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(traveler.createdAt)}
                      </p>
                    </div>
                    <span className="text-green-600 dark:text-green-400 font-semibold">
                      {formatCurrency(traveler.totalAmount)}
                    </span>
                  </div>
                </div>
              )}
              
              {/* Show message if no payments */}
              {((traveler.paymentType === 'cash' && traveler.remainingBalance > 0) || 
                (traveler.paymentType === 'installments' && (!traveler.paymentPlan || traveler.paymentPlan.paidInstallments === 0))) && (
                <div className="text-center py-4">
                  <p className="text-gray-500 dark:text-gray-400">{t('noPaymentsYet')}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Payment Information */}
        <div className="space-y-6">
          {/* Payment Status Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FaDollarSign className="text-green-600" />
              {t('paymentStatus')}
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('status')}:</span>
                <div className="flex items-center gap-2">
                  {getPaymentStatusIcon(paymentStatus)}
                  <span className="font-medium text-gray-900 dark:text-white">
                    {t(`paymentStatus.${paymentStatus}`)}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('totalAmount')}:</span>
                <span className="font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(traveler.totalAmount)}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('paidAmount')}:</span>
                <span className="font-semibold text-green-600 dark:text-green-400">
                  {formatCurrency(traveler.totalAmount - traveler.remainingBalance)}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('remainingBalance')}:</span>
                <span className={`font-semibold ${
                  traveler.remainingBalance === 0 
                    ? 'text-green-600 dark:text-green-400' 
                    : 'text-red-600 dark:text-red-400'
                }`}>
                  {formatCurrency(traveler.remainingBalance)}
                </span>
              </div>
              
              {/* Payment Progress */}
              <div className="mt-4">
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <span>{t('paymentProgress')}</span>
                  <span>{getPaymentProgress()}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${getPaymentProgress()}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Type Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              {traveler.paymentType === 'cash' ? <FaMoneyBillWave className="text-green-600" /> : <FaCreditCard className="text-blue-600" />}
              {t('paymentType')}
            </h3>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('type')}:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {t(`paymentTypes.${traveler.paymentType}`)}
                </span>
              </div>
              
              {/* Installment Plan Details */}
              {traveler.paymentType === 'installments' && traveler.paymentPlan && (
                <>
                  <hr className="border-gray-200 dark:border-gray-600" />
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{t('downPayment')}:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatCurrency(traveler.paymentPlan.downPayment)}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{t('monthlyAmount')}:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {formatCurrency(traveler.paymentPlan.monthlyAmount)}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{t('duration')}:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {traveler.paymentPlan.durationMonths} {t('months')}
                      </span>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">{t('paidInstallments')}:</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {traveler.paymentPlan.paidInstallments || 0} / {traveler.paymentPlan.durationMonths}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Next Payment Card (for installments) */}
          {nextPayment && nextPayment.remaining > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-yellow-800 dark:text-yellow-200 mb-4 flex items-center gap-2">
                <FaCalendarAlt />
                {t('nextPayment')}
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-yellow-700 dark:text-yellow-300">{t('dueDate')}:</span>
                  <span className="font-medium text-yellow-900 dark:text-yellow-100">
                    {formatDate(nextPayment.nextDate)}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-yellow-700 dark:text-yellow-300">{t('amount')}:</span>
                  <span className="font-semibold text-yellow-900 dark:text-yellow-100">
                    {formatCurrency(nextPayment.amount)}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-yellow-700 dark:text-yellow-300">{t('remainingInstallments')}:</span>
                  <span className="font-medium text-yellow-900 dark:text-yellow-100">
                    {nextPayment.remaining}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Quick Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <FaChartLine className="text-blue-600" />
              {t('quickStats')}
            </h3>
            
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('createdOn')}:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatDate(traveler.createdAt)}
                </span>
              </div>
              
              {traveler.updatedAt !== traveler.createdAt && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('lastUpdated')}:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatDate(traveler.updatedAt)}
                  </span>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600 dark:text-gray-400">{t('travelerId')}:</span>
                <span className="font-mono text-xs text-gray-900 dark:text-white">
                  {traveler.id}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelerDetails;